'use client';

import * as React from 'react';
import { Check } from 'lucide-react';

interface PlanRow {
  code: string;
  children: number;
  devices: number;
  priceTryMonthly: string;
  priceUsdMonthly: string;
  priceTryYearly: string;
  priceUsdYearly: string;
  highlighted?: boolean;
}

interface PlanDetails {
  label: string;
  blurb: string;
  features: readonly string[];
}

export interface PricingTableProps {
  plans: readonly PlanRow[];
  details: Record<string, PlanDetails>;
  ctaUrl: string;
}

type Currency = 'TRY' | 'USD';
type Cycle = 'monthly' | 'yearly';

const SYMBOL: Record<Currency, string> = { TRY: '₺', USD: '$' };

export function PricingTable({ plans, details, ctaUrl }: PricingTableProps): JSX.Element {
  const [currency, setCurrency] = React.useState<Currency>('TRY');
  const [cycle, setCycle] = React.useState<Cycle>('monthly');

  function priceOf(plan: PlanRow): string {
    if (currency === 'TRY') {
      return cycle === 'monthly' ? plan.priceTryMonthly : plan.priceTryYearly;
    }
    return cycle === 'monthly' ? plan.priceUsdMonthly : plan.priceUsdYearly;
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
        <Toggle
          options={[
            { value: 'TRY', label: '₺ TL' },
            { value: 'USD', label: '$ USD' },
          ]}
          value={currency}
          onChange={(v) => setCurrency(v as Currency)}
          ariaLabel="Para birimi"
        />
        <Toggle
          options={[
            { value: 'monthly', label: 'Aylık' },
            { value: 'yearly', label: 'Yıllık · 2 ay bedava' },
          ]}
          value={cycle}
          onChange={(v) => setCycle(v as Cycle)}
          ariaLabel="Fatura dönemi"
        />
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
        {plans.map((plan) => {
          const detail = details[plan.code];
          const price = priceOf(plan);
          const period = cycle === 'monthly' ? '/ay' : '/yıl';
          return (
            <article
              key={plan.code}
              className={
                'relative flex flex-col rounded-2xl border bg-white p-7 shadow-sm transition ' +
                (plan.highlighted
                  ? 'border-indigo-600 shadow-indigo-200/60 ring-2 ring-indigo-600/20'
                  : 'border-slate-200 hover:border-indigo-300 hover:shadow-md')
              }
            >
              {plan.highlighted ? (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-gradient-to-r from-indigo-600 to-cyan-500 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-white shadow">
                  En popüler
                </span>
              ) : null}
              <header>
                <p className="text-xs font-semibold uppercase tracking-widest text-indigo-600">
                  {detail.label}
                </p>
                <h3 className="mt-3 flex items-baseline gap-1">
                  <span className="text-4xl font-extrabold tracking-tight text-slate-900">
                    {SYMBOL[currency]}
                    {price}
                  </span>
                  <span className="text-sm font-medium text-slate-500">{period}</span>
                </h3>
                <p className="mt-2 text-sm text-slate-600">{detail.blurb}</p>
                <div className="mt-3 flex gap-2 text-xs text-slate-500">
                  <span className="rounded-full bg-slate-100 px-2 py-0.5">
                    {plan.children} çocuk
                  </span>
                  <span className="rounded-full bg-slate-100 px-2 py-0.5">
                    {plan.devices} cihaz
                  </span>
                </div>
              </header>

              <ul className="mt-6 space-y-2.5 text-sm text-slate-700">
                {detail.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-2">
                    <Check
                      className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600"
                      aria-hidden="true"
                    />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              <a
                href={ctaUrl}
                className={
                  'mt-7 inline-block rounded-full px-5 py-3 text-center text-sm font-bold transition ' +
                  (plan.highlighted
                    ? 'bg-gradient-to-r from-indigo-600 to-cyan-500 text-white shadow hover:-translate-y-0.5 hover:shadow-lg'
                    : 'border border-slate-300 bg-white text-slate-900 hover:border-indigo-300 hover:text-indigo-600')
                }
              >
                14 gün ücretsiz başla
              </a>
            </article>
          );
        })}
      </div>

      <p className="text-center text-xs text-slate-500">
        Tüm fiyatlar KDV dahildir. İptali tek tık, kart bilgisi alınmaz.
      </p>
    </div>
  );
}

interface ToggleOption {
  value: string;
  label: string;
}

interface ToggleProps {
  options: readonly ToggleOption[];
  value: string;
  onChange: (value: string) => void;
  ariaLabel: string;
}

function Toggle({ options, value, onChange, ariaLabel }: ToggleProps): JSX.Element {
  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className="inline-flex rounded-full border border-slate-200 bg-white p-1 shadow-sm"
    >
      {options.map((opt) => {
        const selected = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onChange(opt.value)}
            className={
              'rounded-full px-4 py-1.5 text-xs font-semibold transition ' +
              (selected
                ? 'bg-gradient-to-r from-indigo-600 to-cyan-500 text-white shadow'
                : 'text-slate-600 hover:text-slate-900')
            }
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
